import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { StoreSettings } from '../types/storeSettings';

// البيانات الافتراضية الصحيحة
const CORRECT_STORE_DATA = {
  storeName: 'اليكترونيات السواح',
  storeEmail: 'info@alsawah-electronics.com',
  storePhone: '0123456789',
  storeAddress: 'العنوان، المدينة، البلد',
  storeDescription: 'متخصصون في بيع وصيانة جميع أنواع الأجهزة الإلكترونية والكهربائية بأفضل الأسعار وأعلى جودة.',
  socialLinks: {
    facebook: 'https://facebook.com/alsawah-electronics',
    twitter: 'https://twitter.com/alsawah-electronics',
    instagram: 'https://instagram.com/alsawah-electronics',
    youtube: 'https://youtube.com/alsawah-electronics',
    whatsapp: 'https://wa.me/0123456789'
  },
  workingHours: {
    saturday: '9:00 صباحًا - 9:00 مساءً',
    sunday: '9:00 صباحًا - 9:00 مساءً',
    monday: '9:00 صباحًا - 9:00 مساءً',
    tuesday: '9:00 صباحًا - 9:00 مساءً',
    wednesday: '9:00 صباحًا - 9:00 مساءً',
    thursday: '9:00 صباحًا - 9:00 مساءً',
    friday: '2:00 مساءً - 9:00 مساءً'
  },
  isStoreEnabled: true
};

// مفاتيح التخزين المحلي
const CACHE_KEYS = {
  STORE_SETTINGS: 'storeSettings_cache',
  LAST_UPDATE: 'storeSettings_lastUpdate',
  CACHE_DURATION: 5 * 60 * 1000 // 5 دقائق
};

// دالة للحصول على البيانات من التخزين المحلي
export const getCachedStoreData = (): StoreSettings | null => {
  try {
    if (typeof localStorage === 'undefined') return null;
    
    const cachedData = localStorage.getItem(CACHE_KEYS.STORE_SETTINGS);
    const lastUpdate = localStorage.getItem(CACHE_KEYS.LAST_UPDATE);
    
    if (!cachedData || !lastUpdate) return null;
    
    const cacheAge = Date.now() - parseInt(lastUpdate);
    if (cacheAge > CACHE_KEYS.CACHE_DURATION) {
      // البيانات منتهية الصلاحية
      localStorage.removeItem(CACHE_KEYS.STORE_SETTINGS);
      localStorage.removeItem(CACHE_KEYS.LAST_UPDATE);
      return null;
    }
    
    const settings = JSON.parse(cachedData);
    console.log('📦 استخدام البيانات المخزنة محلياً');
    return settings;
    
  } catch (error) {
    console.error('خطأ في قراءة البيانات المخزنة:', error);
    return null;
  }
};

// دالة لحفظ البيانات في التخزين المحلي
export const cacheStoreData = (settings: StoreSettings): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    
    localStorage.setItem(CACHE_KEYS.STORE_SETTINGS, JSON.stringify(settings));
    localStorage.setItem(CACHE_KEYS.LAST_UPDATE, Date.now().toString());
    console.log('💾 تم حفظ البيانات في التخزين المحلي');
    
  } catch (error) {
    console.error('خطأ في حفظ البيانات:', error);
  }
};

// دالة محسنة لتحميل إعدادات المتجر
export const loadStoreSettingsReliably = async (): Promise<StoreSettings> => {
  console.log('🔄 بدء تحميل إعدادات المتجر...');
  
  // 1. محاولة الحصول على البيانات من التخزين المحلي أولاً
  const cachedData = getCachedStoreData();
  if (cachedData) {
    // تحميل البيانات من Firebase في الخلفية لتحديث التخزين المحلي
    loadFromFirebaseInBackground();
    return cachedData;
  }
  
  // 2. تحميل من Firebase
  try {
    console.log('🔥 تحميل من Firebase...');
    const storeRef = doc(db, 'settings', 'store');
    const storeDoc = await getDoc(storeRef);
    
    if (storeDoc.exists()) {
      const data = storeDoc.data() as StoreSettings;
      console.log('✅ تم تحميل البيانات من Firebase');
      
      // التأكد من وجود البيانات الأساسية
      const completeData = ensureCompleteData(data);
      
      // حفظ في التخزين المحلي
      cacheStoreData(completeData);
      
      return completeData;
    } else {
      console.log('📄 لا توجد إعدادات، إنشاء البيانات الافتراضية...');
      return createDefaultSettings();
    }
    
  } catch (error) {
    console.error('❌ خطأ في تحميل البيانات من Firebase:', error);
    
    // 3. في حالة الفشل، استخدم البيانات الافتراضية
    return createDefaultSettings();
  }
};

// دالة لضمان اكتمال البيانات
const ensureCompleteData = (data: any): StoreSettings => {
  const completeData = {
    general: {
      ...CORRECT_STORE_DATA,
      ...data.general
    },
    shipping: data.shipping || {
      enableShipping: true,
      shippingMethods: [],
      shippingRegions: [],
      defaultShippingMethod: 'standard',
      freeShippingThreshold: 500,
      enablePickup: true,
      pickupLocations: []
    },
    payment: data.payment || {
      enableCashOnDelivery: true,
      enableBankTransfer: true,
      enableElectronicPayment: true,
      bankAccounts: [],
      paymentGateways: []
    },
    notification: data.notification || {
      enableEmailNotifications: true,
      enableSmsNotifications: true,
      enablePushNotifications: true,
      notifyOnNewOrder: true,
      notifyOnOrderStatusChange: true,
      notifyOnLowStock: true,
      adminEmails: ['admin@alsawah-electronics.com'],
      emailTemplates: {}
    },
    display: data.display || {
      theme: 'default',
      productsPerPage: 12,
      showOutOfStockProducts: false,
      showProductRatings: true,
      showRelatedProducts: true,
      enableWishlist: true,
      enableProductComparison: true,
      banners: [],
      promotions: [],
      showBanners: true,
      showPromotions: true
    },
    order: data.order || {
      minimumOrderAmount: 50,
      maximumOrderItems: 20,
      allowGuestCheckout: true,
      requirePhoneNumber: true,
      autoConfirmOrders: false,
      orderNumberPrefix: 'AS',
      orderCancellationPeriod: 24,
      returnPeriod: 7,
      returnPolicy: 'يمكن إرجاع المنتجات خلال 7 أيام من تاريخ الاستلام'
    },
    productSuggestion: data.productSuggestion || {
      isEnabled: true,
      buttonText: 'أخبرنا إذا كان يباع بسعر أقل أو اقترح سعراً',
      destinationType: 'system',
      destinationValue: ''
    },
    bosta: data.bosta || {
      enabled: false,
      apiKey: '',
      defaultPickupCity: '',
      defaultPickupZone: '',
      defaultPickupDistrict: '',
      defaultPickupStreet: '',
      defaultPickupBuilding: '',
      defaultPickupPhone: '',
      deliveryType: '15',
      enableCOD: true
    },
    lastUpdated: data.lastUpdated || new Date()
  };
  
  return completeData as StoreSettings;
};

// دالة لإنشاء البيانات الافتراضية
const createDefaultSettings = (): StoreSettings => {
  console.log('🏗️ إنشاء البيانات الافتراضية...');
  
  const defaultSettings = ensureCompleteData({
    general: CORRECT_STORE_DATA
  });
  
  // حفظ في التخزين المحلي
  cacheStoreData(defaultSettings);
  
  return defaultSettings;
};

// دالة لتحميل البيانات من Firebase في الخلفية
const loadFromFirebaseInBackground = async (): Promise<void> => {
  try {
    const storeRef = doc(db, 'settings', 'store');
    const storeDoc = await getDoc(storeRef);
    
    if (storeDoc.exists()) {
      const data = storeDoc.data() as StoreSettings;
      const completeData = ensureCompleteData(data);
      cacheStoreData(completeData);
      console.log('🔄 تم تحديث التخزين المحلي في الخلفية');
    }
  } catch (error) {
    console.error('خطأ في التحديث الخلفي:', error);
  }
};

// دالة لمراقبة التغييرات في الوقت الفعلي
export const setupRealtimeStoreListener = (
  onUpdate: (settings: StoreSettings) => void
): (() => void) => {
  console.log('👁️ بدء مراقبة التغييرات في الوقت الفعلي...');
  
  const storeRef = doc(db, 'settings', 'store');
  
  const unsubscribe = onSnapshot(storeRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data() as StoreSettings;
      const completeData = ensureCompleteData(data);
      
      // تحديث التخزين المحلي
      cacheStoreData(completeData);
      
      // إشعار المكونات بالتحديث
      onUpdate(completeData);
      
      console.log('🔄 تم تحديث البيانات من Firebase');
    }
  }, (error) => {
    console.error('خطأ في مراقبة التغييرات:', error);
  });
  
  return unsubscribe;
};

// دالة لمسح التخزين المحلي
export const clearStoreCache = (): void => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(CACHE_KEYS.STORE_SETTINGS);
      localStorage.removeItem(CACHE_KEYS.LAST_UPDATE);
      console.log('🗑️ تم مسح التخزين المحلي');
    }
  } catch (error) {
    console.error('خطأ في مسح التخزين المحلي:', error);
  }
};

// إتاحة الدوال من وحدة التحكم
(window as any).getCachedStoreData = getCachedStoreData;
(window as any).loadStoreSettingsReliably = loadStoreSettingsReliably;
(window as any).clearStoreCache = clearStoreCache;

export default {
  loadStoreSettingsReliably,
  getCachedStoreData,
  cacheStoreData,
  setupRealtimeStoreListener,
  clearStoreCache
};
